import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getNavigationTrailForPath } from '@/config/appNavigation'
import { useAssistantContextStore } from '@features/assistant/stores/useAssistantContextStore'
import { useOptionalRouteCatalog } from '@features/navigation/components/RouteCatalogContext'
import { routeSegmentLabel } from '@features/navigation/logic/appRouteCatalog'
import { findRouteParamSource } from '@features/navigation/logic/routeParamSources'
import { describeFavorite, FAVORITE_NAME_SEPARATOR } from '@features/sidebar/logic/favorites'

/** The parts, without one that repeats the part before it. */
function withoutRepeats(parts: readonly string[]) {
  return parts.filter((part, index) => part !== parts[index - 1])
}

/**
 * Names the page at an address for a share, the way the app names an address
 * in favorites and the Home Page setting: `Automations › Runs › All Runs`,
 * `Tables › Accounts › All Records`. A page the route catalog leaves out (a
 * run, a new automation) is named by its module and page header's title.
 */
export function useDescribeSharedPage(): (pathname: string) => Promise<string> {
  const root = useOptionalRouteCatalog()
  const queryClient = useQueryClient()

  return useCallback(
    async (pathname: string) => {
      if (root) {
        const description = await describeFavorite(root, pathname, {
          // The user is on the page, so its area is open to them.
          isAreaAvailable: () => true,
          segmentLabel: routeSegmentLabel,
          loadParamOptions: async (paramNode, params) =>
            (await findRouteParamSource(paramNode.pattern)?.loadOptions(params, queryClient)) ??
            null,
        })
        if (description.status === 'ok') return description.name
      }
      const header = useAssistantContextStore.getState().header?.value ?? null
      const title = header?.title
        ? header.activeTab
          ? `${header.title} (${header.activeTab})`
          : header.title
        : null
      const parts = [...getNavigationTrailForPath(pathname), ...(title ? [title] : [])]
      return withoutRepeats(parts).join(FAVORITE_NAME_SEPARATOR)
    },
    [queryClient, root]
  )
}
